/** @jsx jsx */
/* istanbul ignore file */
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/core';
import Joi from 'joi';
import moment from 'moment';
import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoMdMail } from 'react-icons/io';
import { RiUserFill } from 'react-icons/ri';
import { jsx } from 'theme-ui';
import Config from '../../../firebase/Config';
import i18n from '../../../locales/i18n';
import { useStoreActions, useStoreState } from '../../../models/hooks';
import colors from '../../../styles/colors';
import { emailSchema } from '../../../utils/emailValidationUtils';
import {
  clearErrorMessage,
  errorFormat,
  FormattedError,
  getErrorMessage,
  hasErrorMessage,
  updateObject,
} from '../../../utils/FormErrorUtils';
import { Label } from '../../common';
import { SeekerExistsInfoModal } from '../login/SeekerExistsInfoModal';
import { loginButtonStyle, loginFormTopText } from '../login/loginForm/LoginForm.styles';
import { OnBoardingUserInfoType } from '../userProfileInfoType';

export type SignUpFormProps = {
  isSMB?: boolean;
};

export const SignUpView = ({ isSMB }: SignUpFormProps): JSX.Element => {
  const { t } = useTranslation(['login', 'app']);
  const [isLoadingBtn, setIsLoadingBtn] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<FormattedError[]>([]);
  const [onBoardingUserInfo, setOnBoardingUserInfo] = useState<OnBoardingUserInfoType>({
    firstName: '',
    lastName: '',
    email: '',
  });

  const { updateUserDetail, initializeAsEmployer, employerOnboarded } = useStoreActions((actions) => actions.user);
  const { setIsExistingSeeker } = useStoreActions((actions) => actions.app);
  const { checkUserExists } = useStoreActions((actions) => actions.account);
  const appUserId = useStoreState((s) => s.auth.firebaseUser?.uid);
  const appUserPhoneNumber = useStoreState((s) => s.auth.firebaseUser?.phoneNumber);
  const currentAccountInvite = useStoreState((state) => state.account.currentAccountInvite);
  const isExistingSeeker = useStoreState((state) => state.app.isExistingSeeker);
  const [isSeekerExistsModalOpen, setIsSeekerExistsModalOpen] = useState(false);
  const seekerFirstName = useStoreState((state) => state.app.seekerFirstName);

  useEffect(() => {
    if (isExistingSeeker) {
      setIsSeekerExistsModalOpen(true);
    }
  }, [isExistingSeeker]);

  const schema = Joi.object()
    .options({ abortEarly: false })
    .keys({
      firstName: Joi.string()
        .max(30)
        .required()
        .messages({
          'string.empty': t('signUp.firstNameRequiredMessage'),
        }),
      lastName: Joi.string()
        .max(30)
        .required()
        .messages({
          'string.empty': t('signUp.lastNameRequiredMessage'),
        }),
      email: emailSchema(t),

      phone: Joi.string().allow('').allow(null).optional(),
    });

  const formValidation = (formData: OnBoardingUserInfoType) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { error } = schema.validate(formData);

    if (error) {
      setFormErrors(errorFormat(error.details));
      return false;
    }

    setFormErrors([]);
    return true;
  };

  const handleChange = (fieldName: string, event: React.ChangeEvent<HTMLInputElement>) => {
    updateObject(onBoardingUserInfo, fieldName, event.target.value);

    setFormErrors(clearErrorMessage(formErrors, fieldName));
    setOnBoardingUserInfo({ ...onBoardingUserInfo });
  };

  const submitOnBoardingUserInformation = async () => {
    if (formValidation(onBoardingUserInfo) && appUserId && appUserPhoneNumber) {
      try {
        setIsLoadingBtn(true);
        let accountInviteId = currentAccountInvite?.inviteId;
        if (accountInviteId) {
          const userExists = await checkUserExists({ userId: appUserId });

          if (userExists) {
            accountInviteId = undefined;
          }
        }
        await initializeAsEmployer({
          userId: appUserId,
          employerInitializeData: {
            accountInviteId,
            phoneNumber: appUserPhoneNumber,
            locale: moment.locale(i18n.language),
            timeOffset: moment.parseZone(new Date()).utcOffset(),
            smb: isSMB || undefined,
          },
        });
        await employerOnboarded({ userId: appUserId });
        await updateUserDetail({ userId: appUserId, userDetail: onBoardingUserInfo });
        setIsExistingSeeker(false);
      } finally {
        setIsLoadingBtn(false);
      }
    }
  };

  const getApplyBaseUrl = () => {
    switch (Config.env) {
      case 'beta':
        return 'https://appyhere-webapply-beta.web.app/';
      case 'qa':
        return 'https://appyhere-webapply-qa.web.app/';
      case 'production':
        return 'https://apply.appyhere.com/';
      default:
        // For development, local, test, etc.
        return 'https://appyhere-webapply-dev.web.app/';
    }
  };

  const renderSeekerExistsMessage = () => {
    if (!isSeekerExistsModalOpen) {
      return undefined;
    }

    const heading = seekerFirstName
      ? t('seeker_exists_heading_with_name', { firstName: seekerFirstName })
      : // istanbul ignore next
        t('seeker_exists_heading_without_name');

    const subheading = t('seeker_exists_subheading');

    const handleClose = () => setIsSeekerExistsModalOpen(false);

    const handleFindJob = () => {
      window.location.assign(getApplyBaseUrl());
    };

    const handleCreateRecruiterAccount = () => {
      handleClose();
    };

    return (
      <SeekerExistsInfoModal
        isOpen={isSeekerExistsModalOpen}
        onClose={handleClose}
        heading={heading}
        subheading={subheading}
        onFindJob={handleFindJob}
        onCreateRecruiterAccount={handleCreateRecruiterAccount}
      />
    );
  };

  return (
    <Fragment>
      <Box data-testid="SignUpForm" mt={10}>
        <Flex flexDirection="column">
          <Box css={loginFormTopText} mb={8}>
            <Label size="lg" whiteSpace="initial" mb={1}>
              {t('signUp.signUpTitle')}
            </Label>
            <Label size="base" color={colors.blue.default} whiteSpace="initial">
              {t('signUp.signUpSubTitle')}
            </Label>
          </Box>
          <Box>
            <Flex mb={4}>
              <FormControl mr={4} isInvalid={hasErrorMessage(formErrors, 'firstName')}>
                <FormLabel>{t('signUp.firstName')}</FormLabel>
                <InputGroup>
                  <InputLeftElement p="0" pointerEvents="none">
                    <RiUserFill color={colors.gray['300']} />
                  </InputLeftElement>
                  <Input
                    className="input-type"
                    key="FirstNameInput"
                    autoFocus
                    data-testid="FirstNameInput"
                    type="text"
                    maxLength={14}
                    placeholder={t('signUp.firstName')}
                    onChange={(e) => handleChange('firstName', e)}
                  />
                </InputGroup>
                <FormErrorMessage>{getErrorMessage(formErrors, 'firstName')}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={hasErrorMessage(formErrors, 'lastName')}>
                <FormLabel>{t('signUp.lastName')}</FormLabel>
                <Input
                  className="input-type"
                  key="LastNameInput"
                  data-testid="LastNameInput"
                  type="text"
                  maxLength={14}
                  placeholder={t('signUp.lastName')}
                  onChange={(e) => handleChange('lastName', e)}
                />
                <FormErrorMessage>{getErrorMessage(formErrors, 'lastName')}</FormErrorMessage>
              </FormControl>
            </Flex>
            <FormControl isInvalid={hasErrorMessage(formErrors, 'email')}>
              <FormLabel>{t('signUp.emailAddress')}</FormLabel>
              <InputGroup mb={4}>
                <InputLeftElement p="0" pointerEvents="none">
                  <IoMdMail color={colors.gray['300']} />
                </InputLeftElement>
                <Input
                  className="input-type"
                  key="EmailAddressInput"
                  data-testid="EmailAddressInput"
                  type="email"
                  placeholder={t('signUp.emailAddress')}
                  onChange={(e) => handleChange('email', e)}
                />
              </InputGroup>
              <FormErrorMessage>{getErrorMessage(formErrors, 'email')}</FormErrorMessage>
            </FormControl>
            <Flex justifyContent="end">
              <Button
                w="100%"
                isLoading={isLoadingBtn}
                data-testid="SubmitUserInfoButton"
                css={loginButtonStyle}
                onClick={submitOnBoardingUserInformation}
              >
                {t('signUp.submitText')}
              </Button>
            </Flex>
          </Box>
        </Flex>
      </Box>
      {renderSeekerExistsMessage()}
    </Fragment>
  );
};
